import { useCallback, useEffect, useRef } from 'react';
import type { ScaleDomain } from './scanScale';
import type { ContainerRef } from './useContainerWidth';

/** Wheel delta to zoom factor: one notch (100) is ~1.28x. */
const ZOOM_SENSITIVITY = 0.0025;

interface WheelZoomOptions {
    viewport: ScaleDomain;
    width: number;
    zoomAt: (time: number, factor: number) => void;
    pan: (deltaMs: number) => void;
}

/**
 * Zooms the track on wheel, anchored on the date under the cursor, and pans on
 * horizontal wheel. The listener is native and non-passive because React routes
 * wheel through a passive root listener, where preventDefault is ignored and the
 * page would scroll away under the zoom.
 */
export function useWheelZoom<T extends HTMLElement>(options: WheelZoomOptions): ContainerRef<T> {
    const latest = useRef(options);
    useEffect(() => {
        latest.current = options;
    });

    const detach = useRef<(() => void) | null>(null);

    return useCallback((node: T | null) => {
        detach.current?.();
        detach.current = null;
        if (!node) return;

        const onWheel = (event: WheelEvent) => {
            const { viewport, width, zoomAt, pan } = latest.current;
            if (width <= 0) return;
            const span = viewport.end - viewport.start;

            // Trackpads send horizontal intent as deltaX; honouring it as a pan
            // keeps two-finger swipe meaning what it means everywhere else.
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                event.preventDefault();
                pan((event.deltaX / width) * span);
                return;
            }
            if (event.deltaY === 0) return;
            event.preventDefault();

            const offset = event.clientX - node.getBoundingClientRect().left;
            const anchor = viewport.start + (Math.min(Math.max(offset, 0), width) / width) * span;
            zoomAt(anchor, Math.exp(event.deltaY * ZOOM_SENSITIVITY));
        };

        node.addEventListener('wheel', onWheel, { passive: false });
        detach.current = () => node.removeEventListener('wheel', onWheel);
    }, []);
}
